// Large files, encrypted in pieces. A file is split into fixed 1 MiB chunks and each chunk is
// sealed on its own with AES-256-GCM, so neither sending nor opening a file needs all of it in
// memory, and a video can play from the chunks that have arrived.
//
// Format (MediaRef version 2):
// - One random 256-bit key and one random 96-bit base IV per file, as in version 1.
// - Chunk i uses the base IV with its last 4 bytes XORed with i, big-endian. No two chunks of a
//   file share an IV, and the key is never used for another file.
// - Each chunk's additional data is "rhema.media.v2", then i as 4 bytes big-endian, then 1 for the
//   last chunk and 0 for the others. Moving, repeating or dropping chunks makes decryption fail,
//   and so does cutting the file short, because the new last chunk is not marked last.
// - The stored object is the sealed chunks one after the other. Each is the chunk plus a 16 byte
//   tag, so chunk i starts at i * (chunk_size + 16) and any byte range maps to whole chunks.
// - The ref's sha256 is the SHA-256 of the SHA-256 digests of every sealed chunk, in order. A full
//   download checks it. A ranged read relies on each chunk's tag.

import type { MediaRef } from "./content";
import { fromBase64, MAX_MEDIA_BYTES, MediaIntegrityError, MediaTooLargeError, toBase64 } from "./media";

export const MEDIA_VERSION = 2;
export const CHUNK_BYTES = 1024 * 1024;
export const TAG_BYTES = 16;
/** Chunks in one upload part. 8 MiB parts are above the S3 minimum of 5 MiB. */
export const CHUNKS_PER_PART = 8;
/** Files from this size go up in chunks. Smaller ones stay version 1, which every app can open. */
export const CHUNKED_FROM_BYTES = 16 * 1024 * 1024;

const AAD_LABEL = new TextEncoder().encode("rhema.media.v2");

/** A file Rhema cannot open because a newer app sent it. */
export class MediaVersionError extends Error {
  constructor(readonly version: number) {
    super("Update Rhema to open this file.");
  }
}

/** Whether this app can open a ref: "ok", or "update" when a newer app made it. */
export function mediaSupport(ref: Pick<MediaRef, "version">): "ok" | "update" {
  const v = ref.version ?? 1;
  return v === 1 || v === MEDIA_VERSION ? "ok" : "update";
}

export function isChunked(ref: Pick<MediaRef, "version">): boolean {
  return ref.version === MEDIA_VERSION;
}

/** How a chunked file is laid out. All sizes are in bytes. */
export type Layout = { size: number; chunkSize: number };

export function chunkCount({ size, chunkSize }: Layout): number {
  return Math.max(1, Math.ceil(size / chunkSize));
}

/** Plain bytes in chunk i. */
export function plainChunkBytes(layout: Layout, i: number): number {
  const n = chunkCount(layout);
  if (i < 0 || i >= n) throw new RangeError(`No chunk ${i}`);
  return i < n - 1 ? layout.chunkSize : layout.size - (n - 1) * layout.chunkSize;
}

/** Size of the stored object. */
export function cipherSize(layout: Layout): number {
  return layout.size + chunkCount(layout) * TAG_BYTES;
}

/** Where sealed chunks first..last sit in the stored object, as an inclusive byte range. */
export function cipherRange(layout: Layout, first: number, last: number): [number, number] {
  const sealed = layout.chunkSize + TAG_BYTES;
  return [first * sealed, Math.min(cipherSize(layout), (last + 1) * sealed) - 1];
}

/** The chunks that hold plain bytes start..end, inclusive. */
export function chunksFor(layout: Layout, start: number, end: number): [number, number] {
  return [Math.floor(start / layout.chunkSize), Math.floor(end / layout.chunkSize)];
}

/**
 * Reads an HTTP Range header against a file of `size` bytes. Gives an inclusive [start, end], null
 * for no header or a header this does not handle (so the whole file is sent), or "unsatisfiable".
 * Only single ranges are supported, which is all media elements ask for.
 */
export function parseRange(
  header: string | null | undefined,
  size: number,
): [number, number] | null | "unsatisfiable" {
  if (!header) return null;
  const m = /^bytes=(\d*)-(\d*)$/.exec(header.trim());
  if (!m) return null;
  const [, a, b] = m;
  if (a === "" && b === "") return null;
  if (size === 0) return "unsatisfiable";
  if (a === "") {
    const suffix = Number(b);
    if (suffix === 0) return "unsatisfiable";
    return [Math.max(0, size - suffix), size - 1];
  }
  const start = Number(a);
  if (start >= size) return "unsatisfiable";
  const end = b === "" ? size - 1 : Math.min(Number(b), size - 1);
  if (end < start) return "unsatisfiable";
  return [start, end];
}

export function chunkIv(base: Uint8Array, index: number): Uint8Array<ArrayBuffer> {
  if (base.length !== 12) throw new Error("The IV must be 12 bytes.");
  const iv = new Uint8Array(base);
  const view = new DataView(iv.buffer);
  view.setUint32(8, view.getUint32(8) ^ index);
  return iv;
}

export function chunkAad(index: number, last: boolean): Uint8Array<ArrayBuffer> {
  const aad = new Uint8Array(AAD_LABEL.length + 5);
  aad.set(AAD_LABEL);
  const view = new DataView(aad.buffer);
  view.setUint32(AAD_LABEL.length, index);
  aad[AAD_LABEL.length + 4] = last ? 1 : 0;
  return aad;
}

async function digest(bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
}

/** The ref's sha256 for a chunked file: SHA-256 over every sealed chunk's SHA-256, in order. */
export async function rootHash(digests: Uint8Array[]): Promise<string> {
  const all = new Uint8Array(digests.length * 32);
  digests.forEach((d, i) => {
    all.set(d, i * 32);
  });
  return toBase64(await digest(all));
}

/** Reads bytes start (inclusive) to end (exclusive) of a file, without loading the rest. */
export interface ByteSource {
  readonly size: number;
  read(start: number, end: number): Promise<Uint8Array<ArrayBuffer>>;
}

/** A Blob or File as a ByteSource. Blob.slice reads only the bytes asked for. */
export function blobSource(blob: Blob): ByteSource {
  return {
    size: blob.size,
    read: async (start, end) => new Uint8Array(await blob.slice(start, end).arrayBuffer()),
  };
}

function asBuffer(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  return bytes.buffer instanceof ArrayBuffer ? (bytes as Uint8Array<ArrayBuffer>) : new Uint8Array(bytes);
}

/** Seals and opens the chunks of one file. */
export class ChunkCipher {
  readonly layout: Layout;

  private constructor(
    private readonly key: CryptoKey,
    readonly rawKey: Uint8Array,
    readonly baseIv: Uint8Array,
    size: number,
    chunkSize: number,
  ) {
    this.layout = { size, chunkSize };
  }

  /** A cipher for a new file with a fresh key and IV, or for a ref or saved upload. */
  static async create(
    size: number,
    opts: { fileKey?: string; iv?: string; chunkSize?: number } = {},
  ): Promise<ChunkCipher> {
    if (!Number.isInteger(size) || size < 0) throw new RangeError("Bad file size");
    const chunkSize = opts.chunkSize ?? CHUNK_BYTES;
    if (!Number.isInteger(chunkSize) || chunkSize < 1024 || chunkSize > 16 * 1024 * 1024) {
      throw new MediaIntegrityError();
    }
    if (cipherSize({ size, chunkSize }) > MAX_MEDIA_BYTES) throw new MediaTooLargeError(size);
    const rawKey = opts.fileKey ? fromBase64(opts.fileKey) : crypto.getRandomValues(new Uint8Array(32));
    const baseIv = opts.iv ? fromBase64(opts.iv) : crypto.getRandomValues(new Uint8Array(12));
    if (rawKey.length !== 32 || baseIv.length !== 12) throw new MediaIntegrityError();
    const key = await crypto.subtle.importKey("raw", rawKey, "AES-GCM", false, ["encrypt", "decrypt"]);
    return new ChunkCipher(key, rawKey, baseIv, size, chunkSize);
  }

  static fromRef(
    ref: Pick<MediaRef, "file_key" | "iv" | "size" | "chunk_size" | "version">,
  ): Promise<ChunkCipher> {
    if (!isChunked(ref)) throw new MediaVersionError(ref.version ?? 1);
    return ChunkCipher.create(ref.size, { fileKey: ref.file_key, iv: ref.iv, chunkSize: ref.chunk_size });
  }

  get count(): number {
    return chunkCount(this.layout);
  }

  async seal(index: number, plain: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
    if (plain.byteLength !== plainChunkBytes(this.layout, index))
      throw new RangeError(`Chunk ${index} is the wrong size`);
    const sealed = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: chunkIv(this.baseIv, index),
        additionalData: chunkAad(index, index === this.count - 1),
      },
      this.key,
      asBuffer(plain),
    );
    return new Uint8Array(sealed);
  }

  /** Opens one sealed chunk. Throws MediaIntegrityError if it was changed, moved or cut short. */
  async open(index: number, sealed: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
    if (index < 0 || index >= this.count) throw new MediaIntegrityError();
    if (sealed.byteLength !== plainChunkBytes(this.layout, index) + TAG_BYTES)
      throw new MediaIntegrityError();
    try {
      const plain = await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: chunkIv(this.baseIv, index),
          additionalData: chunkAad(index, index === this.count - 1),
        },
        this.key,
        asBuffer(sealed),
      );
      return new Uint8Array(plain);
    } catch {
      throw new MediaIntegrityError();
    }
  }

  /** Opens sealed chunks first..last, which arrive as one run of bytes. */
  async openRun(first: number, sealedRun: Uint8Array): Promise<Uint8Array<ArrayBuffer>[]> {
    const out: Uint8Array<ArrayBuffer>[] = [];
    let offset = 0;
    for (let i = first; offset < sealedRun.byteLength; i++) {
      if (i >= this.count) throw new MediaIntegrityError();
      const len = plainChunkBytes(this.layout, i) + TAG_BYTES;
      out.push(await this.open(i, sealedRun.subarray(offset, offset + len)));
      offset += len;
    }
    return out;
  }

  /**
   * Reads and seals the chunks of one upload part (part numbers start at 1). Gives the part's bytes
   * and each sealed chunk's SHA-256 digest. Only this part is read from the source.
   */
  async sealPart(
    source: ByteSource,
    part: number,
    chunksPerPart = CHUNKS_PER_PART,
  ): Promise<{ body: Uint8Array<ArrayBuffer>; digests: Uint8Array[]; first: number }> {
    const first = (part - 1) * chunksPerPart;
    const last = Math.min(this.count, first + chunksPerPart) - 1;
    if (first > last) throw new RangeError(`No part ${part}`);
    const [cStart, cEnd] = cipherRange(this.layout, first, last);
    const body = new Uint8Array(cEnd - cStart + 1);
    const digests: Uint8Array[] = [];
    let offset = 0;
    for (let i = first; i <= last; i++) {
      const start = i * this.layout.chunkSize;
      const plain = await source.read(start, start + plainChunkBytes(this.layout, i));
      const sealed = await this.seal(i, plain);
      body.set(sealed, offset);
      offset += sealed.byteLength;
      digests.push(await digest(sealed));
    }
    return { body, digests, first };
  }

  get fileKey(): string {
    return toBase64(this.rawKey);
  }

  get iv(): string {
    return toBase64(this.baseIv);
  }
}

/** Number of upload parts for a file, and the size of every part but the last, in stored bytes. */
export function partLayout(
  layout: Layout,
  chunksPerPart = CHUNKS_PER_PART,
): { parts: number; partBytes: number } {
  return {
    parts: Math.ceil(chunkCount(layout) / chunksPerPart),
    partBytes: chunksPerPart * (layout.chunkSize + TAG_BYTES),
  };
}

/** Fetches stored bytes start..end, inclusive. */
export type RangeFetcher = (
  start: number,
  end: number,
  signal?: AbortSignal,
) => Promise<Uint8Array<ArrayBuffer>>;

/**
 * A RangeFetcher over a presigned URL. It asks for a fresh URL once when the old one has expired.
 * Storage that ignores Range is accepted only when it sent exactly the bytes asked for.
 */
export function urlRangeFetcher(
  url: () => Promise<string>,
  doFetch: typeof fetch = (...args) => fetch(...args),
): RangeFetcher {
  let current: Promise<string> | null = null;
  return async (start, end, signal) => {
    for (let attempt = 0; ; attempt++) {
      current ??= url();
      const href = await current;
      const res = await doFetch(href, {
        headers: { Range: `bytes=${start}-${end}` },
        credentials: "omit",
        signal,
      });
      if ((res.status === 403 || res.status === 401) && attempt === 0) {
        current = null;
        continue;
      }
      if (res.status !== 206 && res.status !== 200)
        throw new Error("This file could not be downloaded. Try again.");
      const bytes = new Uint8Array(await res.arrayBuffer());
      if (res.status === 200 && start === 0 && bytes.byteLength === end + 1) return bytes;
      if (res.status === 200) throw new Error("This file could not be downloaded. Try again.");
      if (bytes.byteLength !== end - start + 1) throw new MediaIntegrityError();
      return bytes;
    }
  };
}

export type StreamOptions = {
  /** First and last plain byte to give, inclusive. The whole file when left out. */
  start?: number;
  end?: number;
  /** Chunks fetched per request. */
  window?: number;
  signal?: AbortSignal;
};

/**
 * Plain bytes of a chunked file as a stream. Chunks are fetched a window at a time, with the next
 * window already on its way while the current one is read, and opened as they arrive. At most two
 * windows are held at once. A whole-file stream also checks the ref's hash at the end.
 */
export function decryptStream(
  ref: Pick<MediaRef, "file_key" | "iv" | "size" | "chunk_size" | "version" | "sha256">,
  fetchRange: RangeFetcher,
  opts: StreamOptions = {},
): ReadableStream<Uint8Array<ArrayBuffer>> {
  const start = opts.start ?? 0;
  const end = opts.end ?? ref.size - 1;
  const whole = start === 0 && end === ref.size - 1;
  const windowChunks = Math.max(1, opts.window ?? 4);
  let cipher: ChunkCipher;
  let next = 0;
  let last = 0;
  const digests: Uint8Array[] = [];
  let ahead: Promise<Uint8Array<ArrayBuffer>> | null = null;
  const ctl = new AbortController();
  opts.signal?.addEventListener("abort", () => ctl.abort(opts.signal?.reason), { once: true });

  const fetchWindow = (from: number) => {
    const to = Math.min(last, from + windowChunks - 1);
    const [s, e] = cipherRange(cipher.layout, from, to);
    return fetchRange(s, e, ctl.signal);
  };

  return new ReadableStream<Uint8Array<ArrayBuffer>>(
    {
      async start() {
        cipher = await ChunkCipher.fromRef(ref);
        if (ref.size === 0) {
          next = 0;
          last = 0;
          return;
        }
        if (start < 0 || end >= ref.size || end < start) throw new RangeError("Bad range");
        [next, last] = chunksFor(cipher.layout, start, end);
        ahead = fetchWindow(next);
      },
      async pull(controller) {
        if (ref.size === 0) {
          // One empty chunk still carries a tag, so even an empty file is checked.
          const [s, e] = cipherRange(cipher.layout, 0, 0);
          const sealed = await fetchRange(s, e, ctl.signal);
          await cipher.open(0, sealed);
          if (whole) digests.push(await digest(sealed));
          if (whole && (await rootHash(digests)) !== ref.sha256) throw new MediaIntegrityError();
          controller.close();
          return;
        }
        const run = await (ahead as Promise<Uint8Array<ArrayBuffer>>);
        const from = next;
        const to = Math.min(last, from + windowChunks - 1);
        next = to + 1;
        ahead = next <= last ? fetchWindow(next) : null;
        // Avoid an unhandled rejection if the stream is cancelled before this is read.
        ahead?.catch(() => undefined);
        if (whole) {
          let offset = 0;
          for (let i = from; i <= to; i++) {
            const len = plainChunkBytes(cipher.layout, i) + TAG_BYTES;
            digests.push(await digest(run.subarray(offset, offset + len)));
            offset += len;
          }
        }
        const plains = await cipher.openRun(from, run);
        for (let k = 0; k < plains.length; k++) {
          const i = from + k;
          let plain = plains[k] as Uint8Array<ArrayBuffer>;
          const chunkStart = i * cipher.layout.chunkSize;
          const lo = Math.max(0, start - chunkStart);
          const hi = Math.min(plain.byteLength, end - chunkStart + 1);
          if (lo > 0 || hi < plain.byteLength) plain = plain.subarray(lo, hi);
          if (plain.byteLength) controller.enqueue(plain);
        }
        if (next > last) {
          if (whole && (await rootHash(digests)) !== ref.sha256) throw new MediaIntegrityError();
          controller.close();
        }
      },
      cancel(reason) {
        ctl.abort(reason);
      },
    },
    { highWaterMark: 0 },
  );
}

/** Reads a whole stream into a Blob. Parts stay separate, so no one buffer holds the file. */
export async function streamToBlob(
  stream: ReadableStream<Uint8Array<ArrayBuffer>>,
  type: string,
): Promise<Blob> {
  const parts: Uint8Array<ArrayBuffer>[] = [];
  const reader = stream.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
  }
  return new Blob(parts, { type });
}
